import { useEffect, useState } from 'react';
import type { AddressRepo } from '@/core/repos/address.repo';
import { wardLabel } from '../address/useProvinceWards';
import type { FilterOptions } from './FilterBar';

/** Options belong to the current selection, independent of page template initialization. */
export function useAddressFilterChoices(repo: AddressRepo, enabled: boolean, provinceId?: string, wardId?: string, factoryScoped = false) {
    const [provinces, setProvinces] = useState<FilterOptions[]>([]);
    const [wardResult, setWardResult] = useState<{ scope: string; options: FilterOptions[] }>({ scope: '', options: [] });
    const [loadingScope, setLoadingScope] = useState<string>();
    const scope = JSON.stringify([provinceId || '', wardId || '', factoryScoped]);

    useEffect(() => {
        if (!enabled) return;
        let active = true;
        repo.getAllProvinces(provinceId).then(entries => {
            if (active) setProvinces(entries.map(([id, province]) => ({ value: id, name: province.name })));
        }).catch(() => { if (active) setProvinces([]); });
        return () => { active = false; };
    }, [repo, enabled, provinceId]);

    useEffect(() => {
        if (!enabled || !provinceId) return;
        let active = true;
        setLoadingScope(scope);
        const loadWards = factoryScoped
            ? repo.getFactoryWardsByProvinceId(provinceId, wardId)
            : repo.getWardsByProvinceId(provinceId, wardId);
        loadWards.then(wards => {
            if (active) setWardResult({ scope, options: wards.map(ward => ({ value: ward.id, name: wardLabel(ward) })) });
        }).catch(() => { if (active) setWardResult({ scope, options: [] }); })
            .finally(() => { if (active) setLoadingScope(undefined); });
        return () => { active = false; };
    }, [repo, enabled, provinceId, wardId, scope, factoryScoped]);

    return {
        provinces,
        wards: provinceId && wardResult.scope === scope ? wardResult.options : [],
        loadingWards: !!provinceId && (loadingScope === scope || wardResult.scope !== scope),
    };
}
